import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="logo">
            <Link to="/" className="logo-link">
              <h1>Pebble</h1>
            </Link>
          </div>
          <nav className="nav">
            <Link to="/" className="nav-link">Home</Link>
            <Link to="/privacy" className="nav-link">Privacy</Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="legal-content">
        <div className="container">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last updated: August 2026</p>

          <section>
            <h2>Agreement to Terms</h2>
            <p>
              By downloading, installing, or using the Pebble mobile application ("Service"), you agree to be bound 
              by these Terms of Service ("Terms"). If you disagree with any part of these terms, then you may not 
              access the Service.
            </p>
          </section>

          <section>
            <h2>Description of Service</h2>
            <p>
              Pebble is a baby tracking application that allows parents and caregivers to:
            </p>
            <ul>
              <li>Track daily activities including feeding, sleeping, and diaper changes</li>
              <li>Upload and store photos to document your baby's growth</li>
              <li>Record developmental milestones</li>
              <li>Share access with family members and caregivers</li>
              <li>View insights and patterns in your baby's routines</li>
            </ul>
          </section>

          <section>
            <h2>User Accounts and Responsibility</h2>
            
            <h3>Account Creation</h3>
            <ul>
              <li>You must be at least 18 years old to create an account</li>
              <li>You must provide accurate and complete information</li>
              <li>You are responsible for maintaining the security of your account</li>
              <li>You must notify us immediately of any unauthorized use</li>
            </ul>

            <h3>Acceptable Use</h3>
            <p>You agree to use Pebble only for its intended purpose of tracking your baby's activities. You must not:</p>
            <ul>
              <li>Upload inappropriate, harmful, or illegal content</li>
              <li>Share access with unauthorized individuals</li>
              <li>Attempt to reverse engineer or compromise the app's security</li>
              <li>Use the service for any commercial purposes without permission</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>
          </section>

          <section>
            <h2>Content and Data</h2>
            
            <h3>Your Content</h3>
            <p>
              You retain ownership of all content you upload to Pebble, including photos, notes, and tracking data. 
              By uploading content, you grant us the right to store, process, and display this content solely for 
              the purpose of providing our service to you.
            </p>

            <h3>Prohibited Content</h3>
            <p>You may not upload content that:</p>
            <ul>
              <li>Violates any laws or regulations</li>
              <li>Infringes on intellectual property rights</li>
              <li>Contains harmful, offensive, or inappropriate material</li>
              <li>Violates the privacy of others</li>
            </ul>

            <h3>Content Removal</h3>
            <p>
              We reserve the right to remove any content that violates these terms or is otherwise objectionable, 
              though we are not obligated to monitor or review content.
            </p>
          </section>

          <section>
            <h2>Privacy and Data Use</h2>
            <p>
              Your privacy is important to us. Please review our <Link to="/privacy">Privacy Policy</Link> to 
              understand how we collect, use, and protect your information. Key points include:
            </p>
            <ul>
              <li>We never share or sell your personal photos</li>
              <li>We may use anonymized, aggregated data for research and insights</li>
              <li>You control who has access to your baby's information</li>
              <li>You can delete your data at any time</li>
            </ul>
          </section>

          <section>
            <h2>Subscription and Payments</h2>
            
            <h3>Free and Premium Features</h3>
            <p>
              Pebble offers both free and premium subscription features. Premium features may include unlimited 
              photo storage, advanced analytics, and additional sharing options.
            </p>

            <h3>Billing and Cancellation</h3>
            <ul>
              <li>Subscriptions are billed automatically through your app store</li>
              <li>You can cancel at any time through your app store settings</li>
              <li>Cancellations take effect at the end of the current billing period</li>
              <li>No refunds are provided for partial subscription periods</li>
            </ul>
          </section>

          <section>
            <h2>Disclaimer of Medical Advice</h2>
            <div className="medical-disclaimer">
              <h3>⚠️ Important Medical Disclaimer</h3>
              <p>
                <strong>Pebble is not a medical device and does not provide medical advice.</strong> The information 
                tracked in Pebble is for personal record-keeping only and should not be used as a substitute for 
                professional medical advice, diagnosis, or treatment.
              </p>
              <p>
                Always consult with your pediatrician or healthcare provider about your baby's health, feeding 
                patterns, sleep concerns, or any other medical questions. If you have concerns about your baby's 
                health or development, contact your healthcare provider immediately.
              </p>
            </div>
          </section>

          <section>
            <h2>Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Pebble and its creators shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages, including without limitation, loss of profits, 
              data, use, goodwill, or other intangible losses, resulting from your use of the service.
            </p>
          </section>

          <section>
            <h2>Service Availability</h2>
            <p>
              We strive to keep Pebble available 24/7, but we cannot guarantee uninterrupted access. We may:
            </p>
            <ul>
              <li>Perform maintenance that temporarily affects service availability</li>
              <li>Make changes to features or functionality</li>
              <li>Discontinue the service with reasonable notice</li>
            </ul>
          </section>

          <section>
            <h2>Termination</h2>
            <p>
              Either party may terminate this agreement at any time. Upon termination:
            </p>
            <ul>
              <li>Your access to the service will be discontinued</li>
              <li>You may export your data before termination</li>
              <li>We will delete your data according to our Privacy Policy</li>
              <li>Certain provisions of these terms will survive termination</li>
            </ul>
          </section>

          <section>
            <h2>Changes to Terms</h2>
            <p>
              We may modify these terms from time to time. We will notify you of any significant changes through 
              the app or via email. Your continued use of Pebble after changes become effective constitutes 
              acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2>Governing Law</h2>
            <p>
              These terms shall be governed by and construed in accordance with the laws of the State of New Jersey,
              USA, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2>Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us at:
            </p>
            <p>
              <strong>Email:</strong> legal@pebble.baby<br/>
              <strong>Subject Line:</strong> Terms of Service Inquiry
            </p>
          </section>

          <div className="back-to-home">
            <Link to="/" className="btn btn-primary">Back to Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;